import React, { useEffect, useRef } from "react";
import { UiIcon } from "shared/uikit";
import { ICONS } from "shared/constants";
import { PPDWidgetItemValueProgress } from "../../../PPDWidgetItemValueProgress";
import { formatValueWithUnits } from "../../../../../../utilities/formatValueWithUnits";
import { getProgressColor } from "../../../../../../utilities/getStatusColor";
import { useDashboardDict } from '../../../../../../context/useDashboardDict';
import classnames from 'classnames';
import { useMedia } from 'shared/hooks';

import "./styles.scss"

type Props = {
    list: any[];
    activeTab: "plan" | "sales";
    subtab: "leaders" | "my_place";
    className?: string;
    isModal?: boolean;
};

const getDirectionColor = (direction: string) => {
    switch (direction) {
        case "up": return "#16a34a";
        case "down": return "#f43f5e";
        default: return "#2c2f35";
    }
};

const MEDAL_IMAGES: Record<string, string> = {
    bronze: require("./../../../../assets/medal_bronze.svg").default,
    silver: require("./../../../../assets/medal_silver.svg").default,
    gold: require("./../../../../assets/medal_gold.svg").default,
};

const WINGS_IMAGES: Record<string, string>  = {
    bronze: require("./../../../../assets/wings_bronze.svg").default,
    silver: require("./../../../../assets/wings_silver.svg").default,
    gold: require("./../../../../assets/wings_gold.svg").default,
};

export const PPDWidgetRatingLeagueList = ({
    list,
    activeTab,
    subtab,
    className,
    isModal
}: Props) => {
    const listRef = useRef<HTMLUListElement | null>(null);
    const itemRefs = useRef<Record<number, HTMLLIElement | null>>({});

    const { employees, units } = useDashboardDict() || {};
    const { isMobileOrTablet } = useMedia()

    const getEmployeeName = (id: number) =>
        employees?.find((e) => e.id === id)?.name ?? "-";

    useEffect(() => {
        const list = listRef.current;
        if (!list) return;

        const items = Object.values(itemRefs.current);
        const firstItem = items[0];
        const currentItem = items.find(el => el?.dataset.current === "true");

        const scrollToElement = (element: HTMLElement | undefined | null, block: ScrollLogicalPosition = "center") => {
            element?.scrollIntoView({
                behavior: "smooth",
                block,
            });
        };

        const scrollToPosition = (target: number) => {
            list.scrollTo({
                top: Math.max(0, target),
                behavior: "smooth",
            });
        };

        if (subtab === "leaders") {
            if (isModal) {
                scrollToElement(firstItem);
            } else {
                scrollToPosition(0);
            }
            return;
        }

        if (!currentItem) return;

        if (isModal) {
            scrollToElement(currentItem);
        } else {
            const itemRect = currentItem.getBoundingClientRect();
            const listRect = list.getBoundingClientRect();
            const itemHeight = itemRect.height;

            const relativeTop = itemRect.top - listRect.top;
            const offsetMultiplier = isMobileOrTablet ? 1 : 4;
            const targetScroll = list.scrollTop + relativeTop - itemHeight * offsetMultiplier;

            scrollToPosition(targetScroll);
        }
    }, [subtab, isModal, isMobileOrTablet]);

    return (
        <ul className={classnames("ppd-widget-league-rating-list", className)} ref={listRef}>
            {list.map((item, index) => (
                <li
                    key={`${item.place}-${index}`}
                    className={`ppd-widget-league-rating-list__item ${
                        item.is_current ? "ppd-widget-league-rating-list__item--current" : ""
                    }`}
                    ref={(el) => (itemRefs.current[item.place] = el)}
                    data-current={item.is_current ? "true" : "false"}
                >
                    <div className="ppd-widget-league-rating-list__user">
                        <div className="ppd-widget-league-rating-list__medal-wrapper">
                            {["gold", "silver", "bronze"].includes(item.league) ? (
                                <div className="ppd-widget-league-rating-list__medal">
                                    <img
                                        src={MEDAL_IMAGES[item.league]}
                                        alt={`${item.league} medal`}
                                        className="ppd-widget-league-rating-list__medal-image"
                                    />
                                    {item.is_league_leader && (
                                        <img
                                            src={WINGS_IMAGES[item.league]}
                                            alt="wings"
                                            className="ppd-widget-league-rating-list__medal-wings"
                                        />
                                    )}
                                    <span
                                        className={`ppd-widget-league-rating-list__place ppd-widget-league-rating-list__place--${item.league}`}
                                    >
                                        {item.place}
                                    </span>
                                </div>
                            ) : (
                                <div className="ppd-widget-league-rating-list__medal">
                                    {activeTab === "sales" ? (
                                        <div className="ppd-widget-league-rating-list__medal-box">
                                            <span className="ppd-widget-league-rating-list__place">
                                                {item.place}
                                            </span>
                                        </div>
                                    ) : (
                                        <>
                                            <UiIcon
                                                icon={ICONS.LEAGUE_MEDAL}
                                                size={24}
                                                color={getProgressColor(item?.value?.amount, "#2c2f35")}
                                            />
                                            <span className="ppd-widget-league-rating-list__place">
                                                {item.place}
                                            </span>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        <span className="ppd-widget-league-rating-list__name">
                            {item.is_current ? "Вы" : getEmployeeName(item.employee_id)}
                        </span>

                        {item.is_current &&
                            ["up", "down"].includes(item.place_change?.direction) && (
                            <div className="ppd-widget-league-rating-list__change">
                                <UiIcon
                                    icon={
                                        item.place_change.direction === "up"
                                            ? ICONS.MOVE_UP
                                            : ICONS.MOVE_DOWN
                                    }
                                    size={14}
                                    color={getDirectionColor(item.place_change.direction)}
                                    className="ppd-widget-league-rating-list__change-icon"
                                />
                                <span
                                    className="ppd-widget-league-rating-list__change-value"
                                    style={{
                                        color: getDirectionColor(item.place_change.direction),
                                    }}
                                >
                                    {item.place_change.direction === "up"
                                        ? `+${item.place_change.value}`
                                        : `-${item.place_change.value}`
                                    }
                                </span>
                            </div>
                        )}
                    </div>

                    {activeTab === "plan" && (
                        <div className="ppd-widget-league-rating-list__progress">
                            <PPDWidgetItemValueProgress
                                size={20}
                                strokeWidth={4}
                                progress={item?.value?.amount ?? 0}
                                showValue={false}
                            />
                            <span className="ppd-widget-league-rating-list__progress-value">
                                {formatValueWithUnits(item?.value, units)}
                            </span>
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );
};
